import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { extractErr } from '@/lib/api';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { BrandLogo } from '@/components/BrandLogo';

export default function LoginPage() {
  const { login, token } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  React.useEffect(() => { if (token) nav('/vc/leads', { replace: true }); }, [token, nav]);
  React.useEffect(() => {  // keep this page out of search engines and link previews
    const prevTitle = document.title;
    const m = Object.assign(document.createElement('meta'), { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet' });
    m.setAttribute('data-vc', '1');
    document.head.appendChild(m);
    document.title = 'VERSE';
    return () => { m.remove(); document.title = prevTitle; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome back');
      nav('/vc/leads');
    } catch (err) {
      toast.error(extractErr(err, 'Could not sign in.'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[#F5F5F7]">
      {/* Rail */}
      <div className="hidden lg:flex lg:col-span-2 relative overflow-hidden bg-[#1D1D1F]">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1637665759389-41818b867562?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80')" }} />
        <div className="relative z-10 flex flex-col justify-between w-full p-10 text-slate-100">
          <div className="flex items-end gap-3">
            <BrandLogo white className="h-14" testid="login-brand" />
            <span className="text-xs uppercase tracking-widest text-slate-400">Staff</span>
          </div>
          <div className="space-y-3">
            <h1 className="font-serif text-4xl leading-tight">A calmer way to care for your leads.</h1>
            <p className="text-slate-300 max-w-sm text-sm leading-relaxed">Capture enquiries from every channel, triage with intention, and follow up without losing the human touch.</p>
          </div>
          <div className="text-xs text-slate-500">© VERSE Clinic · Kuala Lumpur</div>
        </div>
      </div>

      {/* Form */}
      <div className="lg:col-span-3 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-end gap-2">
            <BrandLogo className="h-12" testid="login-brand-mobile" />
            <span className="text-xs uppercase tracking-widest text-slate-500">Staff</span>
          </div>
          {forgot ? <ForgotPasswordForm onBack={() => setForgot(false)} /> : <>
          <h2 className="font-serif text-3xl text-[#1D1D1F]">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Authorised staff only.</p>
          <form onSubmit={submit} autoComplete="off" className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wide text-slate-500">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" name="verse-login-id" autoComplete="off" autoCapitalize="none" spellCheck={false} data-lpignore="true" data-1p-ignore required
                value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-white" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wide text-slate-500">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" name="verse-login-secret" autoComplete="new-password" data-lpignore="true" data-1p-ignore required
                value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-white" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full bg-[#1D1D1F] hover:bg-[#2C2C2E] text-white transition-colors duration-200">
              {loading ? 'Signing in\u2026' : 'Sign in'}
            </Button>
          </form>
          <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
            <button type="button" onClick={() => setForgot(true)} className="hover:text-[#1D1D1F] transition-colors" data-testid="login-forgot-password">Forgot password?</button>
            <span>Having trouble? Contact your clinic admin.</span>
          </div>
          </>}
        </div>
      </div>
    </div>
  );
}
